import styled from "styled-components";
import { FormControl, Select, MenuItem, Box } from "@mui/material";
import { useLocaleWithStorage, Locale } from "../locales";
import { LanguageFlag } from "../assets/images/languageFlags";
import userStorage from "../utils/userStorage";
import inplabLogo from "../assets/images/inplabLogo.png";
import theme from "../theme";

const { sm, md, lg } = theme.screen;

const getFooterLogo = () => {
  try {
    const logo = require("../assets/images/footer-logo.png"); // 동적 로드
    return { logo, corp: "other" }; // 파일이 있으면 "other"
  } catch (error) {
    return { logo: inplabLogo, corp: "inplab" }; // 파일이 없으면 "inplab"
  }
};

const Footer: React.FC<{}> = () => {
  const { logo: footerLogo, corp } = getFooterLogo(); // 이미지 & corp 값 가져오기
  const { locale, setLocale } = useLocaleWithStorage();
  const user = userStorage.get();

  return (
    <>
      <FooterContainer>
        <Row>
          <RightFixed>
            {user?.area === "00000" && user?.grade === 2 && (
            <LanguageSelectFormControl size="small">
              <Select
                value={locale}
                onChange={(e) => setLocale(e.target.value as Locale)}
                sx={{
                  "& .MuiSelect-select": { padding: "6px 28px 6px 10px", fontSize: "12px" },
                }}
              >
                <MenuItem value="ko">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <LanguageFlag locale="ko" width={16} height={10} />
                    <span>한국어</span>
                  </Box>
                </MenuItem>
                <MenuItem value="en">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <LanguageFlag locale="en" width={16} height={10} />
                    <span>English</span>
                  </Box>
                </MenuItem>
                <MenuItem value="zh">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <LanguageFlag locale="zh" width={16} height={10} />
                    <span>中文</span>
                  </Box>
                </MenuItem>
                <MenuItem value="ja">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <LanguageFlag locale="ja" width={16} height={10} />
                    <span>日本語</span>
                  </Box>
                </MenuItem>
                <MenuItem value="vi">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <LanguageFlag locale="vi" width={16} height={10} />
                    <span>Tiếng Việt</span>
                  </Box>
                </MenuItem>
                <MenuItem value="es">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <LanguageFlag locale="es" width={16} height={10} />
                    <span>Español</span>
                  </Box>
                </MenuItem>
              </Select>
            </LanguageSelectFormControl>
            )}
          </RightFixed>

          <Center>
            <LogoContainer>
              <img src={footerLogo} alt="inplabLogo" />
            </LogoContainer>
            <ContentsContainer $corp={corp}>
              <ContentText>
                {corp === "inplab"
                  ? "T. 054-434-7278"
                  : process.env.REACT_APP_FOOTER_TEL}
              </ContentText>
              <ContentText>
                {corp === "inplab"
                  ? "경상북도 김천시 혁신로 303, 303호 인플랩 주식회사"
                  : process.env.REACT_APP_FOOTER_ADDRESS}
              </ContentText>
              <Copyright>
                {corp === "inplab"
                  ? "Copyright © 2023. Inplab Corp. All Rights Reserved."
                  : process.env.REACT_APP_FOOTER_COPYRIGHT}
              </Copyright>
            </ContentsContainer>
          </Center>
        </Row>
        {/* <ManualContainer>
          <ManualButton>매뉴얼 다운로드</ManualButton>
          <Download />
        </ManualContainer> */}
      </FooterContainer>
    </>
  );
};

const FooterContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 5px;
  padding-bottom: 5px;
  padding-left: 20px;
  padding-right: 20px;
  gap: 20px;
  background-color: ${theme.color.white};
  border-top: 1px ${theme.color.neutral_100} solid;
  position: relative;
`;
const LogoContainer = styled.div`
  img {
    width: 210px;
    height: 70px;
    @media (max-width: ${sm}px) {
      width: 150px;
      height: 50px;
    }
    @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
      width: 210px;
      height: 70px;
    }

    @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
      width: 210px;
      height: 70px;
    }
    @media (min-width: ${lg + 1}px) {
      width: 250px;
      height: 80px;
    }
  }
`;
const ContentsContainer = styled.div<{ $corp: string }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: center;
  padding-top: 10px;

  @media (max-width: ${md}px) {
    align-items: center;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    padding-top: ${({ $corp }) => ($corp === "other" ? "0px" : "10px")};
  }
  @media (min-width: ${lg + 1}px) {
    padding-top: ${({ $corp }) => ($corp === "other" ? "0px" : "20px")};
  }
  /* @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    padding-top: 10px;
  }
  @media (min-width: ${lg + 1}px) {
    padding-top: 20px;
  } */
`;

const ContentText = styled.span`
  color: ${theme.color.neutral_500};
  font-size: 12px;
  @media (max-width: ${sm}px) {
    font-size: 10px;
  }
`;

const Copyright = styled.span`
  color: ${theme.color.neutral_500};
  font-size: 12px;
  @media (max-width: ${sm}px) {
    font-size: 10px;
  }
`;

// Right side container removed in current layout

const LanguageSelectFormControl = styled(FormControl)`
  min-width: 140px;
`;

export default Footer;
const Row = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;

  @media (max-width: ${md}px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const RightFixed = styled.div`
  position: absolute;
  right: 20px;
  @media (max-width: ${md}px) {
    position: static;
    right: auto;
  }
`;
